import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ContactForm, ContactFormData } from '../../components/ContactForm/ContactForm';
import { Loader } from '../../components/Loader/Loader';
import whiteCheckmark from '../../assets/icons/white-checkmark.svg';
import accoladeLogo from '../../assets/Logo-Accolade-dark.svg';
import languageIcon from '../../assets/icons/language-icon-gray.svg';
import './ContactPage.scss';

export const GuestContactPage: React.FC = () => {

  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { t, i18n } = useTranslation('contact');
  
  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
  };

  const handleSubmit = async (data: ContactFormData) => {
    setIsLoading(true);
    try {
      // Import the contact service
      const { contactService } = await import('../../services/contact.service');
      
      try {
        // Submit the form data to Salesforce Web-to-Case
        const response = await contactService.submitWebToCase(data);
        if (response.status === 200 || response.status === 201) {
          setIsSuccess(true);
        }
      } catch (error: any) {
        // Check if this is the expected CORS error (which actually means success)
        if (error.code === 'ERR_NETWORK' && error.config?.url?.includes('salesforce.com')) {
          setIsSuccess(true);
        } else {
          console.error('Error submitting contact form:', error);
        }
      }
    } catch (error) {
      console.error('Error importing contact service:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="contact-page">
        <header className="contact-page__header">
          <div className="contact-page__logo-container">
            <Link to="/auth">
              <img src={accoladeLogo} alt="Accolade" className="contact-page__logo" />
            </Link>          
          </div>
          <button
            className="contact-page__language-toggle"
            onClick={toggleLanguage}
            aria-label={t('nav.changeLanguage')}
          >
            <img src={languageIcon} alt="" />
            <span>{i18n.language.toUpperCase()}</span>
          </button>
        </header>
        <div className="contact-page__success">
          <div className="contact-page__success-icon">
            <img src={whiteCheckmark} alt="Success" />
          </div>
          <div className="contact-page__success-content">
            <h1>{t('success.title')}</h1>
            <p>{t('success.message')}</p>
            <div className="contact-page__button-group">
              <button 
                className="contact-page__button"
                onClick={() => setIsSuccess(false)}
              >
                {t('success.newRequest')}
              </button>
              <Link 
                to="/auth" 
                className="contact-page__button contact-page__button--secondary"
              >
                {t('success.backToLogin')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-page">
      <header className="contact-page__header">
        <div className="contact-page__logo-container">
          <Link to="/auth">
            <img src={accoladeLogo} alt="Accolade" className="contact-page__logo" />
          </Link>
        </div>
        <button
          className="contact-page__language-toggle"
          onClick={toggleLanguage}
          aria-label={t('nav.changeLanguage')}
        >
          <img src={languageIcon} alt="" />
          <span>{i18n.language.toUpperCase()}</span>
        </button>
      </header>
      <div className="contact-page__content">
        {isLoading && <Loader variant="overlay" />}
        <ContactForm isAuthenticated={false} onSubmit={handleSubmit} />
      </div>
    </div>
  );
};
